import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  clearSelectedMedia,
  getCommonState
} from '@ports/common/commonStateSlice';
import MediaPlayer from '@business/workflows/ComponentUI/MediaPlayer';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { SVGAssets } from '@business/assets/vectors';
import {
  useGetCountsQuery,
  useGetLikesQuery,
  useAddLikesMutation,
  useDeleteLikesMutation
} from '@ports/internal-services/likes/likesSlice';
import {
  useGetCommentsQuery,
  useAddCommentsMutation,
  useUpdateCommentsMutation,
  useDeleteCommentsMutation
} from '@ports/internal-services/comment/commentSlice';
import {
  useDeleteAssetsMutation,
  useGetAssetsByIdQuery,
  useGetAssetsPeopleQuery,
  useReassignFaceMutation
} from '@ports/internal-services/asset/assetSlice';
import { useAddFavoritesMutation } from '@ports/internal-services/favorites/favoritesSlice';
import { getUserData } from '@ports/common/userStateSlice';
import {
  getSubscriberId,
  getToken,
  incrementAssetRefreshCounter
} from '@ports/common/sessionStateSlice';
import { getPermissions } from '@ports/common/authStateSlice';
import {
  Comment,
  MediaPostScreenProps,
  MediaType
} from '@business/interfaces/photo';
import { AppLoader, ConfirmationModal } from '@business/workflows/ComponentUI';
import { handleDownloads } from '@utils/helpers/lib/photoHelpers';
import { routePaths } from '@utils/constants/routePaths';
import { SelectFriendFamily } from '@business/workflows/ComponentUI/People/SelectFriendFamily';
import { SearchableSelectInput } from '@business/workflows/ComponentUI/SearchableInput';
import { Button } from '@business/workflows/ComponentUI';
import {
  useAddPeopleAssetMutation,
  useAddPeopleMutation,
  useGetPeopleByNameQuery,
  useUpdatePeopleMutation
} from '@ports/internal-services/people/peopleSlice';
import { useDebounce } from 'src/hooks/Debounce';
import { FriendsForm } from '@business/workflows/ComponentUI/People/FriendsForm';
import { FamilyForm } from '@business/workflows/ComponentUI/People/FamilyForm';
import { MergePerson } from '@business/workflows/ComponentUI/People';
import { useMergePeopleMutation } from '@ports/internal-services/people/peopleSlice';
import { useTranslation } from 'react-i18next';
import { showToast } from '@business/workflows/ComponentUI/Toast/Toast';
import { extractFaceFromImage } from '@utils/helpers/lib/photoHelpers/extractFaceFromImage';
import { uploadToS3 } from '@utils/helpers/s3Upload';
import { ShareModalComponent } from '@business/workflows/ComponentUI/Share';
import DiarySection from './DiarySection';
import MediaInfoSidebar from './MediaInfoSidebar';
import FullScreenModal from './FullScreenModal';
import CommentSection from './CommentSection';
import MediaStats from './MediaStats';
import MediaPostHeader from './MediaPostHeader';

const MediaPostScreen: React.FC<MediaPostScreenProps> = ({
  assetId,
  groupId
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedMedia } = useSelector(getCommonState);
  const userDetails = useSelector(getUserData);
  const userId = userDetails.userId || ''; // Using email as userId, adjust as needed
  const subscriberId = useSelector(getSubscriberId);
  const idToken = useSelector(getToken);
  // Ref-counted loading state: every in-flight action (face reassign/rename/
  // create, merge, and the FriendsForm/FamilyForm submit they can open)
  // pushes/pops this counter, so overlapping async steps share one
  // consistent loader instead of each stage flipping its own boolean on and
  // off out of sync with the others.
  const [loadingCount, setLoadingCount] = useState<number>(0);
  const isLoading = loadingCount > 0;
  const pushLoading = useCallback(() => setLoadingCount(c => c + 1), []);
  const popLoading = useCallback(
    () => setLoadingCount(c => Math.max(0, c - 1)),
    []
  );
  // Adapter matching the boolean setIsLoading(bool) signature FriendsForm/
  // FamilyForm expect, backed by the same push/pop counter.
  const setIsLoading = useCallback(
    (loading: boolean) => {
      if (loading) {
        pushLoading();
      } else {
        popLoading();
      }
    },
    [pushLoading, popLoading]
  );
  const permissions = useSelector(getPermissions);
  const canShare =
    permissions.includes('sharing:update') &&
    selectedMedia?.source !== 'sharing-shared-with-me';

  const canFavorite = selectedMedia?.source !== 'sharing-shared-with-me';

  const canDownload = permissions.includes('photo:download');

  const isSharing = selectedMedia?.source === 'sharing-shared-with-me';

  // Fetch asset data
  const { data: assetData, refetch: refetchAsset } = useGetAssetsByIdQuery(
    { id: assetId },
    { skip: !assetId, refetchOnMountOrArgChange: true, refetchOnFocus: true }
  );
  const canDelete =
    permissions.includes('photo:delete') &&
    selectedMedia?.source !== 'sharing-shared-with-me' &&
    assetData?.data?.mediaOwner;

  // Fetch counts (likes, comments, diary)
  const { data: countsData, refetch: refetchCounts } = useGetCountsQuery(
    { assetId },
    { skip: !assetId, refetchOnMountOrArgChange: true, refetchOnFocus: true }
  );

  // Fetch user's like status
  const { data: likesData, refetch: refetchLikes } = useGetLikesQuery(
    { assetId },
    { skip: !assetId, refetchOnMountOrArgChange: true, refetchOnFocus: true }
  );

  // Fetch comments
  const { data: commentsData, refetch: refetchComments } = useGetCommentsQuery(
    { assetId },
    { skip: !assetId, refetchOnMountOrArgChange: true, refetchOnFocus: true }
  );

  const { data: assetPeopleData, refetch: refetchAssetPeople } =
    useGetAssetsPeopleQuery({ id: assetId }, { skip: !assetId });

  // Mutations
  const [addLike] = useAddLikesMutation();
  const [deleteLike] = useDeleteLikesMutation();
  const [addComment] = useAddCommentsMutation();
  const [updateComment] = useUpdateCommentsMutation();
  const [deleteComment] = useDeleteCommentsMutation();
  const [deleteAsset] = useDeleteAssetsMutation();
  const [addFavorite] = useAddFavoritesMutation();
  const [reassignFace] = useReassignFaceMutation();

  // Local state for optimistic updates
  const [isLiked, setIsLiked] = useState(false);
  const [userLikeId, setUserLikeId] = useState<string | null>(null);
  const [optimisticLikeCount, setOptimisticLikeCount] = useState(0);
  const [optimisticCommentCount, setOptimisticCommentCount] = useState(0);
  const [optimisticDiaryCount, setOptimisticDiaryCount] = useState(0);
  const [optimisticComments, setOptimisticComments] = useState<Comment[]>([]);
  const [isProcessingLike, setIsProcessingLike] = useState<boolean>(false);
  const [isFullScreenOpen, setIsFullScreenOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
  const [isInfoSidebarOpen, setIsInfoSidebarOpen] = useState<boolean>(false);
  const [isDiarySectionOpen, setIsDiarySectionOpen] = useState<boolean>(false);
  const [isCommentSectionOpen, setIsCommentSectionOpen] =
    useState<boolean>(true);
  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  const [isPreviewError, setIsPreviewError] = useState<boolean>(false);

  const [imageDimensions, setImageDimensions] = useState<any>(null);

  // Who is this feature state
  const [activeFaceIndex, setActiveFaceIndex] = useState<number | null>(null);
  const [hoveredFaceIndex, setHoveredFaceIndex] = useState<number | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [isNameSelected, setIsNameSelected] = useState<boolean>(false);
  const [selectedPersonId, setSelectedPersonId] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isSelectFriendFamily, setIsSelectFriendFamily] =
    useState<boolean>(false);
  const [selectedPersonType, setSelectedPersonType] = useState<
    'family' | 'friend' | null
  >(null);
  const [isOpenFriendsForm, setIsOpenFriendsForm] = useState<boolean>(false);
  const [isOpenFamilyForm, setIsOpenFamilyForm] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [createdPersonId, setCreatedPersonId] = useState<string>('');

  // Confirmation modal state for creating a new person when renaming an
  // already-tagged face instead of updating the existing person's name
  const [isConfirmCreateNewPersonOpen, setIsConfirmCreateNewPersonOpen] =
    useState<boolean>(false);
  const [pendingNewPersonFaceIndex, setPendingNewPersonFaceIndex] = useState<
    number | null
  >(null);
  const [pendingNewPersonName, setPendingNewPersonName] = useState<string>('');
  // Tracks which face triggered the "Add Person" style creation flow so the
  // newly created person can be tagged to this asset and given the cropped
  // face image as its profile picture once the create form submits.
  const [faceIndexForNewPerson, setFaceIndexForNewPerson] = useState<
    number | null
  >(null);

  // Merge person modal state
  const [isMergePersonOpen, setIsMergePersonOpen] = useState<boolean>(false);
  const [sourcePersonIdForMerge, setSourcePersonIdForMerge] =
    useState<string>('');
  const [selectedMergePersonId, setSelectedMergePersonId] =
    useState<string>('');
  const [selectedMergePerson, setSelectedMergePerson] = useState<string>('');
  const [newPersonImage, setNewPersonImage] = useState<string>('');
  const [selectedPersonImage, setSelectedPersonImage] = useState<string>('');

  // Ref for face popup to handle outside clicks
  const facePopupRef = useRef<HTMLDivElement>(null);
  const imageViewportScrollRef = useRef<HTMLDivElement>(null);
  const docPreviewRef = useRef<HTMLDivElement>(null);
  // Ref to skip onSearch reset immediately after a dropdown selection
  const justSelectedRef = useRef<boolean>(false);
  const isMountedRef = useRef<boolean>(true);

  // People mutations
  const [MergeMutation] = useMergePeopleMutation();

  // Debounce search for people
  const debouncedSearch = useDebounce(searchText, 400);
  const { data: peopleDataForSearch } = useGetPeopleByNameQuery(
    { search: debouncedSearch },
    { skip: !debouncedSearch }
  );

  const syncMediaData = useCallback(() => {
    refetchAsset();
    refetchCounts();
    refetchLikes();
    refetchComments();
  }, [refetchAsset, refetchComments, refetchCounts, refetchLikes]);

  useEffect(() => {
    if (!assetId) return;
    syncMediaData();
    refetchAssetPeople();
  }, [assetId, refetchAssetPeople, syncMediaData]);

  const familySearchOptions = useMemo(() => {
    return (
      peopleDataForSearch?.data?.rows?.map((item: any) => ({
        id: item?.id,
        name: item?.name,
        profilePicUrl: item?.profileImage,
        userId: item?.userId,
        subscriberId: item?.subscriberId
      })) || []
    );
  }, [peopleDataForSearch?.data?.rows]);

  // Extract media data from asset
  const mediaType = assetData?.data?.contentType || 'image';
  const mediaSrc = assetData?.data?.url || '';
  // Face bounding boxes (array of { boundingBox: { left, top, width, height } })
  const faceDetections = assetData?.data?.faceDetections || [];

  // Determine if the asset is a PDF
  const isPdf =
    mediaType === 'pdf' ||
    mediaSrc.toLowerCase().endsWith('.pdf') ||
    mediaType === 'document';

  const isAudio =
    mediaType?.startsWith('audio/') ||
    mediaSrc.toLowerCase().match(/\.(mp3|wav|ogg|aac)$/);

  const handlePreviewError = useCallback(() => {
    setIsPreviewError(true);
  }, []);

  useEffect(() => {
    setIsPreviewError(false);
  }, [assetId, mediaSrc]);

  useEffect(() => {
    if (!isPdf || isPreviewError) {
      return () => {};
    }

    const previewNode = docPreviewRef.current;

    if (!previewNode) {
      return () => {};
    }

    const failureTextPatterns = [
      'failed to load pdf file',
      'failed to load',
      'error loading',
      'no renderer',
      'unsupported',
      'corrupt',
      'invalid pdf'
    ];

    const hasFailureText = () => {
      const text = previewNode.textContent?.toLowerCase() || '';
      return failureTextPatterns.some(pattern => text.includes(pattern));
    };

    const hasRenderedPreview = () =>
      Boolean(
        previewNode.querySelector(
          'canvas, iframe, object, embed, img[src], video'
        )
      );

    const checkForFailure = () => {
      if (hasFailureText()) {
        handlePreviewError();
      }
    };

    const observer = new MutationObserver(checkForFailure);
    observer.observe(previewNode, {
      childList: true,
      subtree: true,
      characterData: true
    });

    const blankPreviewTimer = window.setTimeout(() => {
      if (!hasRenderedPreview()) {
        handlePreviewError();
      }
    }, 12000);

    checkForFailure();

    return () => {
      observer.disconnect();
      window.clearTimeout(blankPreviewTimer);
    };
  }, [handlePreviewError, isPdf, isPreviewError, mediaSrc]);

  const mergedFaceDetections = useMemo(() => {
    if (!faceDetections?.length || !assetPeopleData?.data?.length) {
      return faceDetections;
    }

    // Create quick lookup map using userId
    const peopleMap = new Map(
      assetPeopleData.data.map((person: any) => [person.userId, person])
    );

    return faceDetections.map((face: any) => {
      const matchedPerson = peopleMap.get(face.userId);

      return {
        ...face,
        peopleId: matchedPerson?.id || null,
        personName: matchedPerson?.name || null,
        profileImage: matchedPerson?.profileImage || null,
        personUserId: matchedPerson?.userId || null,
        personSubscriberId: matchedPerson?.subscriberId || null
      };
    });
  }, [faceDetections, assetPeopleData]);

  // Update favorite status when asset data is fetched
  useEffect(() => {
    if (assetData?.data?.favorite) {
      setIsFavorite(assetData?.data?.favorite?.status === 'Active');
    }
  }, [assetData]);

  // Update like status when data is fetched
  useEffect(() => {
    if (likesData?.data) {
      const likesPayload: any = likesData.data;
      const likeRows = Array.isArray(likesPayload?.userIds)
        ? likesPayload.userIds
        : [];
      const currentUserLike = likeRows?.find(
        (like: any) => like?.userId === userId
      );
      const likedByCurrentUser = Boolean(currentUserLike);

      setIsLiked(likedByCurrentUser);
      setUserLikeId(currentUserLike?.id || likesPayload?.userLikeId || null);
    } else {
      setIsLiked(false);
      setUserLikeId(null);
    }
  }, [likesData, userId]);
  // Update optimistic counts when real data arrives
  useEffect(() => {
    if (countsData?.data) {
      setOptimisticLikeCount(countsData.data.likes || 0);
      setOptimisticCommentCount(countsData.data.comments || 0);
      setOptimisticDiaryCount(countsData.data.diary || 0);
    }
  }, [countsData]);

  // Transform comments data to match Comment interface
  const transformedComments: Comment[] =
    commentsData?.data?.rows?.map(comment => {
      // Keep like status user-scoped and count sourced from backend payload.
      const userCommentLike = comment.commentLikes?.find(
        (like: any) => like.userId === userId
      );

      return {
        id: comment.id,
        name: comment.name,
        profileImage: comment.profileImage,
        text: comment.comment,
        timestamp: comment.createdAt,
        userId: comment.userId,
        commentLikesCount:
          comment.commentLikesCount ?? comment.commentLikes?.length ?? 0,
        commentLikes: comment.commentLikes || [],
        isLiked: Boolean(userCommentLike),
        userCommentLikeId: (userCommentLike?.id || null) as string | null
      };
    }) || [];

  // Update comments when data is fetched
  useEffect(() => {
    setOptimisticComments(transformedComments);
  }, [commentsData, userId]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const displayComments = optimisticComments;

  const handleCommentLikeUpdate = () => {
    // Child handles optimistic UI; parent re-syncs all counters and states.
    setTimeout(() => {
      if (!isMountedRef.current) return;
      syncMediaData();
    }, 100);
  };

  const handleDiaryUpdate = () => {
    // Keep diary list/counts and media stats aligned with backend after writes.
    setTimeout(() => {
      if (!isMountedRef.current) return;
      syncMediaData();
    }, 100);
  };

  const handleFavorite = async () => {
    const previousIsFavorite = isFavorite;

    try {
      // Optimistic update
      setIsFavorite(!isFavorite);

      // Call API
      const status = !isFavorite ? 'Active' : 'InActive';
      await addFavorite({
        createFavorites: {
          userId,
          assetId,
          subscriberId,
          status
        }
      }).then(() => {
        refetchAsset();
      });
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      // Rollback on failure
      setIsFavorite(previousIsFavorite);
    }
  };

  const handleLike = async () => {
    // Prevent multiple rapid clicks
    if (isProcessingLike) return;
    setIsProcessingLike(true);

    // Store previous state for rollback
    const previousIsLiked = isLiked;
    const previousCount = optimisticLikeCount;
    const previousLikeId = userLikeId;

    try {
      // 1. OPTIMISTIC UPDATE - Update UI immediately
      setIsLiked(!isLiked);
      setOptimisticLikeCount(prev => (isLiked ? prev - 1 : prev + 1));

      if (isLiked && userLikeId) {
        // Unlike
        await deleteLike({ id: userLikeId, userId, assetId }).unwrap();
        setUserLikeId(null);
      } else {
        // Like
        const result = await addLike({
          createLikes: { assetId, userId }
        }).unwrap();
        if (result.data?.id) {
          setUserLikeId(result.data.id);
        }
      }

      // 2. BACKGROUND REFRESH - Fetch real data without blocking UI
      setTimeout(() => {
        if (!isMountedRef.current) return;
        syncMediaData();
      }, 100);
    } catch (error) {
      console.error('Failed to toggle like:', error);

      // 3. ROLLBACK on failure
      setIsLiked(previousIsLiked);
      setOptimisticLikeCount(previousCount);
      setUserLikeId(previousLikeId);
    } finally {
      // Allow next click after a short delay
      setTimeout(() => {
        if (!isMountedRef.current) return;
        setIsProcessingLike(false);
      }, 300);
    }
  };

  const handleAddComment = async (text: string) => {
    const optimisticCommentId = `temp-${Date.now()}`;
    const previousComments = [...optimisticComments];
    const previousCount = optimisticCommentCount;
    const nextCommentText = text.trim();

    if (!nextCommentText) return;

    const optimisticComment: Comment = {
      id: optimisticCommentId,
      name: userDetails.fullName || userDetails.email || 'You',
      profileImage: userDetails.profileImageUrl,
      text: nextCommentText,
      timestamp: new Date().toISOString(),
      userId,
      commentLikesCount: 0,
      commentLikeCount: 0,
      commentLikes: [],
      isLiked: false,
      userCommentLikeId: null
    };

    try {
      // Optimistic update for current user.
      setOptimisticComments(prev => [...prev, optimisticComment]);
      setOptimisticCommentCount(prev => prev + 1);

      await addComment({
        createComments: {
          userId,
          assetId,
          comment: nextCommentText
        }
      }).unwrap();

      setTimeout(() => {
        if (!isMountedRef.current) return;
        syncMediaData();
      }, 100);
    } catch (error) {
      console.error('Failed to add comment:', error);

      // Rollback on failure.
      setOptimisticComments(previousComments);
      setOptimisticCommentCount(previousCount);
    }
  };

  const handleUpdateComment = async (commentId: string, text: string) => {
    const nextCommentText = text.trim();
    if (!nextCommentText) return;

    const previousComments = [...optimisticComments];

    try {
      // Optimistic update for current user.
      setOptimisticComments(prev =>
        prev.map(comment =>
          comment.id === commentId
            ? { ...comment, text: nextCommentText }
            : comment
        )
      );

      await updateComment({
        id: commentId,
        updateComments: {
          comment: nextCommentText
        }
      }).unwrap();

      setTimeout(() => {
        if (!isMountedRef.current) return;
        syncMediaData();
      }, 100);
    } catch (error) {
      console.error('Failed to update comment:', error);

      // Rollback on failure.
      setOptimisticComments(previousComments);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    // Store previous state for rollback
    const previousComments = [...optimisticComments];
    const previousCount = optimisticCommentCount;

    try {
      // 1. OPTIMISTIC UPDATE - Remove comment immediately
      setOptimisticComments(prev =>
        prev.filter(comment => comment.id !== commentId)
      );
      setOptimisticCommentCount(prev => prev - 1);

      // 2. API CALL
      await deleteComment({ id: commentId, delete: 'true' }).unwrap();

      // 3. BACKGROUND REFRESH
      setTimeout(() => {
        if (!isMountedRef.current) return;
        syncMediaData();
      }, 100);
    } catch (error) {
      console.error('Failed to delete comment:', error);

      // ROLLBACK - Restore deleted comment
      setOptimisticComments(previousComments);
      setOptimisticCommentCount(previousCount);
    }
  };

  const handleBack = () => {
    dispatch(clearSelectedMedia());
    navigate(-1);
  };

  const handleExpand = () => {
    setIsFullScreenOpen(true);
  };

  const handleShare = () => {
    if (!canShare) {
      return;
    }
    setIsShareModalOpen(true);
  };

  const handleDiaryClick = () => {
    setIsDiarySectionOpen(!isDiarySectionOpen);
    if (!isDiarySectionOpen) {
      setIsCommentSectionOpen(false);
    }
  };

  const handleCommentClick = () => {
    setIsCommentSectionOpen(!isCommentSectionOpen);
    if (!isCommentSectionOpen) {
      setIsDiarySectionOpen(false);
    }
  };

  const handleInfo = () => {
    setIsInfoSidebarOpen(true);
  };

  const handleStar = () => {
    handleFavorite();
  };

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteAsset({ id: assetId }).unwrap();

      // Close modal
      setIsDeleteModalOpen(false);

      // Clear selected media from Redux
      dispatch(clearSelectedMedia());

      // Navigate back to photos page
      navigate(routePaths.Photos);
    } catch (error) {
      console.error('Failed to delete asset:', error);
      // Keep modal open so user can try again or cancel
      // Optionally show error toast/alert here
    }
  };

  // Who is this handlers
  const handleFaceBoxClick = (faceIndex: number) => {
    if (activeFaceIndex === faceIndex) {
      setActiveFaceIndex(null);
      setSearchText('');
      setIsNameSelected(false);
      setSelectedPersonId('');
      setIsEditMode(false);
    } else {
      setActiveFaceIndex(faceIndex);
      setSearchText('');
      setIsNameSelected(false);
      setSelectedPersonId('');
      setIsEditMode(false);
    }
  };

  const handleEditClick = (faceIndex: number, currentName?: string) => {
    const face = mergedFaceDetections[faceIndex];

    setActiveFaceIndex(faceIndex);
    setSearchText(face?.personName || currentName || '');
    setIsNameSelected(false);
    setSelectedPersonId('');
    setIsEditMode(true);
  };

  const [AddFamily] = useAddPeopleMutation();
  const [UpdatePeople] = useUpdatePeopleMutation();
  const [TagPeople] = useAddPeopleAssetMutation();

  const updateExistingPersonName = (personId: string, nextName: string) => {
    const dataObj = {
      id: personId,
      updatePeople: {
        name: nextName
      }
    };

    pushLoading();
    UpdatePeople(dataObj)
      .then((res: any) => {
        if (res?.data) {
          if (isEditMode) {
            setActiveFaceIndex(null);
            setSearchText('');
            setIsNameSelected(false);
            setSelectedPersonId('');
            setIsEditMode(false);
          } else {
            setName(nextName);
            setCreatedPersonId(personId);
            setIsSelectFriendFamily(true);
          }

          refetchAsset();
          refetchAssetPeople();
        } else {
          console.log('Failed to update name');
        }
      })
      .catch(err => {
        console.log('Error updating name:', err);
      })
      .finally(() => {
        popLoading();
      });
  };

  // Once the "Add Person" style create form (FriendsForm/FamilyForm) has
  // created the brand new person: bind this specific face detection to them
  // (via reassignFace, scoped to just this faceDetectionId so it never
  // touches any Person's userId and can't create a duplicate-userId
  // collision), untag whichever person was previously tagged to this face,
  // and tag the new person to this asset instead with the cropped face
  // image as their profile picture. Only runs for the
  // create-new-person-from-rename flow (faceIndexForNewPerson is only set
  // from handleConfirmCreateNewPerson).
  const handleNewPersonCreatedFromFace = useCallback(
    async (peopleId: string) => {
      const faceIndex = faceIndexForNewPerson;
      setFaceIndexForNewPerson(null);

      if (faceIndex === null) return;

      const face = mergedFaceDetections[faceIndex];
      const oldPersonId = face?.peopleId;

      pushLoading();
      try {
        let newPeopleImageUrl = '';
        if (face?.boundingBox) {
          try {
            const imageFile = await extractFaceFromImage(
              mediaSrc,
              face.boundingBox,
              `profile_${assetId}.jpg`
            );
            newPeopleImageUrl = await uploadToS3(
              imageFile,
              idToken,
              userId,
              'profile',
              '',
              {
                userId: face.userId
              }
            );
            if (newPeopleImageUrl) {
              await UpdatePeople({
                id: peopleId,
                updatePeople: { profileImage: newPeopleImageUrl }
              });
            }
          } catch (err) {
            console.log('Error uploading face image:', err);
          }
        }

        if (face?.id) {
          try {
            await reassignFace({
              body: {
                faceDetectionId: face.id,
                newPeopleId: peopleId,
                reindexInRekognition: true,
                ...(newPeopleImageUrl ? { newPeopleImageUrl } : {})
              }
            }).unwrap();
          } catch (err) {
            console.log('Error binding face to new person:', err);
          }
        }

        try {
          // Rebuild the asset's tagged-people list without the old person and
          // with the new person added, then submit it with removeOld so the
          // old person is dropped from this image instead of staying tagged
          // alongside the new one.
          const remainingPeopleIds = (assetPeopleData?.data || [])
            .map((person: any) => person.id)
            .filter((id: string) => id && id !== oldPersonId);

          await TagPeople({
            createPeopleAsset: {
              assetId,
              peopleId: Array.from(new Set([...remainingPeopleIds, peopleId])),
              removeOld: true
            }
          }).unwrap();
          dispatch(incrementAssetRefreshCounter());
        } catch (error) {
          console.error('Error tagging person to asset:', error);
        }

        refetchAsset();
        refetchAssetPeople();
      } finally {
        popLoading();
      }
    },
    [
      faceIndexForNewPerson,
      mergedFaceDetections,
      mediaSrc,
      assetId,
      idToken,
      userId,
      UpdatePeople,
      reassignFace,
      TagPeople,
      assetPeopleData,
      dispatch,
      refetchAsset,
      refetchAssetPeople,
      pushLoading,
      popLoading
    ]
  );

  const handleDoneClick = async (faceIndex: number) => {
    if (isLoading) return;

    const nameToUse = searchText.trim();

    if (!nameToUse) return;

    const face = mergedFaceDetections[faceIndex];
    const sourceId = face?.peopleId;
    if (isEditMode && isNameSelected && selectedPersonId && sourceId) {
      // Edit flow: an already-identified face is being reassigned to a
      // different, existing person picked from the dropdown. Re-link the
      // face detection to that person instead of renaming/merging anyone.
      if (sourceId === selectedPersonId) {
        setActiveFaceIndex(null);
        setSearchText('');
        setIsNameSelected(false);
        setSelectedPersonId('');
        setIsEditMode(false);
        return;
      }

      pushLoading();
      try {
        // If the person we're reassigning this face to has no profile
        // picture yet, use the cropped face from this photo as their
        // profile image instead of leaving it blank. Upload first so the
        // resulting URL can be sent along with the reassign call.
        const reassignedPerson = familySearchOptions.find(
          (person: any) => person.id === selectedPersonId
        );
        let newPeopleImageUrl = '';
        if (!reassignedPerson?.profilePicUrl && face?.boundingBox) {
          try {
            const imageFile = await extractFaceFromImage(
              mediaSrc,
              face.boundingBox,
              `profile_${assetId}.jpg`
            );
            newPeopleImageUrl = await uploadToS3(
              imageFile,
              idToken,
              userId,
              'profile',
              '',
              {
                userId: face?.userId
              }
            );
          } catch (err) {
            console.log(
              'Error uploading face image for reassigned person:',
              err
            );
          }
        }

        await reassignFace({
          body: {
            faceDetectionId: face?.id,
            newPeopleId: selectedPersonId,
            reindexInRekognition: true,
            ...(newPeopleImageUrl ? { newPeopleImageUrl } : {})
          }
        }).unwrap();

        setActiveFaceIndex(null);
        setSearchText('');
        setIsNameSelected(false);
        setSelectedPersonId('');
        setIsEditMode(false);

        refetchAsset();
        refetchAssetPeople();
      } catch (err) {
        console.log('Error reassigning face:', err);
      } finally {
        popLoading();
      }

      return;
    }
    if (!isEditMode && isNameSelected && selectedPersonId && sourceId) {
      // Get selected person's data from familySearchOptions
      const selectedPerson = familySearchOptions.find(
        (person: any) => person.id === selectedPersonId
      );

      // Check if face belongs to current user and use their profile image
      let currentPersonImage = face?.profileImage || '';
      if (
        face?.userId === userId &&
        !currentPersonImage &&
        userDetails.profileImageUrl
      ) {
        currentPersonImage = userDetails.profileImageUrl;
      }

      // Set merge data
      setSourcePersonIdForMerge(sourceId);
      setSelectedMergePersonId(selectedPersonId);
      setName(nameToUse);
      setSelectedMergePerson(selectedPerson?.name || nameToUse);
      setNewPersonImage(currentPersonImage);
      setSelectedPersonImage(selectedPerson?.profilePicUrl || '');

      // Show merge modal
      setIsMergePersonOpen(true);

      return;
    }
    if (!sourceId && isNameSelected && selectedPersonId) {
      // Untagged face: the user picked an existing person from the dropdown
      // instead of typing a brand-new name. There's no previous person on
      // this face to merge with or replace, so just bind this face
      // detection to the selected person directly.
      pushLoading();
      try {
        const selectedPerson = familySearchOptions.find(
          (person: any) => person.id === selectedPersonId
        );
        let newPeopleImageUrl = '';
        if (!selectedPerson?.profilePicUrl && face?.boundingBox) {
          try {
            const imageFile = await extractFaceFromImage(
              mediaSrc,
              face.boundingBox,
              `profile_${assetId}.jpg`
            );
            newPeopleImageUrl = await uploadToS3(
              imageFile,
              idToken,
              userId,
              'profile',
              '',
              {
                userId: face?.userId
              }
            );
          } catch (err) {
            console.log('Error uploading face image for selected person:', err);
          }
        }

        await reassignFace({
          body: {
            faceDetectionId: face?.id,
            newPeopleId: selectedPersonId,
            reindexInRekognition: true,
            ...(newPeopleImageUrl ? { newPeopleImageUrl } : {})
          }
        }).unwrap();

        setActiveFaceIndex(null);
        setSearchText('');
        setIsNameSelected(false);
        setSelectedPersonId('');
        setIsEditMode(false);

        refetchAsset();
        refetchAssetPeople();
      } catch (err) {
        console.log('Error tagging selected person to face:', err);
      } finally {
        popLoading();
      }

      return;
    }
    // Only treat this as a "rename" attempt when the typed name actually
    // differs from the person currently tagged to this face. Re-submitting
    // the same name (e.g. opening edit mode and clicking Done unchanged)
    // should keep behaving like a no-op save, not prompt for a new person.
    const isNameActuallyChanged =
      !!sourceId &&
      !!face?.personName &&
      nameToUse.toLowerCase() !== face.personName.toLowerCase();

    if (sourceId && !isNameSelected && !isEditMode) {
      if (isNameActuallyChanged) {
        // Face already belongs to a tagged person, but the user typed a
        // different name instead of picking one from the dropdown. Rather
        // than silently renaming the existing person, confirm whether they
        // want to create a brand new person with this name instead.
        setPendingNewPersonFaceIndex(faceIndex);
        setPendingNewPersonName(nameToUse);
        setIsConfirmCreateNewPersonOpen(true);
      } else {
        updateExistingPersonName(sourceId, nameToUse);
      }
    } else if (!isNameSelected && isEditMode) {
      if (sourceId) {
        if (isNameActuallyChanged) {
          setPendingNewPersonFaceIndex(faceIndex);
          setPendingNewPersonName(nameToUse);
          setIsConfirmCreateNewPersonOpen(true);
        } else {
          updateExistingPersonName(sourceId, nameToUse);
        }
      }
    } else if (!isNameSelected) {
      const dataObj: any = {
        createPeople: {
          type: null,
          name: nameToUse,
          ...(face?.userId ? { userId: face.userId } : {})
        }
      };

      pushLoading();
      let imageFile;
      try {
        imageFile = await extractFaceFromImage(
          mediaSrc,
          face.boundingBox,
          `profile_${assetId}.jpg`
        );
      } catch (err) {
        console.log('Error extracting face image:', err);
        popLoading();
        return;
      }

      try {
        const imageUrl = await uploadToS3(
          imageFile,
          idToken,
          userId,
          'profile',
          '',
          {
            userId: face?.userId
          }
        );
        if (imageUrl) {
          dataObj.createPeople.profileImage = imageUrl;
        }
      } catch (err) {
        console.log('Error uploading face image:', err);
      }

      AddFamily(dataObj)
        .then(async (res: any) => {
          if (res?.data) {
            const peopleId = res?.data?.data?.id;
            try {
              await TagPeople({
                createPeopleAsset: { assetId, peopleId, removeOld: false }
              }).unwrap();
              dispatch(incrementAssetRefreshCounter());
            } catch (error) {
              console.error('Error tagging person to asset:', error);
            }
            setName(nameToUse);
            setCreatedPersonId(peopleId);
            refetchAsset();
            refetchAssetPeople();
            setIsSelectFriendFamily(true);
          } else {
            console.log('Failed to update name');
          }
        })
        .catch(err => {
          console.log('Error updating name:', err);
        })
        .finally(() => {
          popLoading();
        });
    }
  };

  const handleConfirmCreateNewPerson = () => {
    const faceIndex = pendingNewPersonFaceIndex;
    const nextName = pendingNewPersonName;

    setIsConfirmCreateNewPersonOpen(false);
    setPendingNewPersonFaceIndex(null);
    setPendingNewPersonName('');

    if (faceIndex === null || !nextName) return;

    // Follow the same "Add Person" flow as People.tsx: open the family/friend
    // type picker with no createdPersonId so FriendsForm/FamilyForm perform a
    // real create, instead of eagerly creating a stub person here.
    setFaceIndexForNewPerson(faceIndex);
    setCreatedPersonId('');
    setName(nextName);
    setIsSelectFriendFamily(true);
  };

  const handleCancelCreateNewPerson = () => {
    setIsConfirmCreateNewPersonOpen(false);
    setPendingNewPersonFaceIndex(null);
    setPendingNewPersonName('');
  };

  const handleMergePerson = async () => {
    if (isLoading) return;
    pushLoading();
    try {
      const dataObj = {
        mergePeople: {
          sourcePersonId: sourcePersonIdForMerge,
          targetPersonId: selectedMergePersonId,
          deleteSource: true
        }
      };

      await MergeMutation(dataObj).unwrap();

      // Close merge modal
      setIsMergePersonOpen(false);

      // Refresh data
      refetchAsset();
      refetchAssetPeople();

      // Reset states
      setActiveFaceIndex(null);
      setSearchText('');
      setIsNameSelected(false);
      setSelectedPersonId('');
      setIsEditMode(false);
      setSourcePersonIdForMerge('');
      setSelectedMergePersonId('');
      setSelectedMergePerson('');
      setNewPersonImage('');
      setSelectedPersonImage('');

      // Show success message
      showToast({
        type: 'success',
        isAlert: true,
        alertProps: {
          type: 'success',
          message: 'People merged successfully!'
        }
      });
    } catch (err) {
      console.log('Error merging people:', err);
      showToast({
        type: 'error',
        isAlert: true,
        alertProps: {
          type: 'error',
          message: 'Failed to merge people.'
        }
      });
    } finally {
      popLoading();
    }
  };

  const handleFriendFamilyConfirm = (type: 'family' | 'friend') => {
    setSelectedPersonType(type);
    setIsSelectFriendFamily(false);
    if (type === 'family') {
      setIsOpenFamilyForm(true);
    } else if (type === 'friend') {
      setIsOpenFriendsForm(true);
    }
  };

  const peopleFiltersHandler = () => {
    // Refetch asset data to update face detections with newly identified people
    refetchAsset();
    refetchAssetPeople();
  };

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      const imgEl = e.currentTarget;
      // Get image element and its dimensions
      let scaleX = 1,
        scaleY = 1,
        offsetLeft = 0,
        offsetTop = 0;
      let renderedW = 1,
        renderedH = 1,
        naturalW = 1,
        naturalH = 1;
      if (imgEl) {
        renderedW = imgEl.clientWidth;
        renderedH = imgEl.clientHeight;
        naturalW = imgEl.naturalWidth;
        naturalH = imgEl.naturalHeight;
        scaleX = renderedW / naturalW;
        scaleY = renderedH / naturalH;
        // If image is centered, get offset
        const rect = imgEl.getBoundingClientRect();
        const parentRect = imgEl.parentElement?.getBoundingClientRect();
        if (parentRect) {
          offsetLeft = rect.left - parentRect.left;
          offsetTop = rect.top - parentRect.top;
        }
      }
      setImageDimensions({
        scaleX,
        scaleY,
        offsetLeft,
        offsetTop,
        renderedW,
        renderedH,
        naturalW,
        naturalH
      });
    },
    []
  );

  return (
    <div className="min-h-screen bg-[var(--media-page-bg)] text-gray-100">
      {/* Single overlay loader for every in-flight action on this screen
          (face reassign/rename/create, merge, FriendsForm/FamilyForm
          submit). Rendered inline instead of replacing the whole tree so
          the underlying view/modals stay mounted and don't flicker. */}
      <AppLoader isLoading={isLoading} />
      <TransformWrapper
        initialScale={1}
        minScale={1}
        maxScale={mediaType === 'image' && !isPdf ? 5 : 1}
        disabled={mediaType !== 'image' && !isPdf}
        wheel={{ disabled: false, excluded: ['face-popup'] }}
        pinch={{ disabled: false, excluded: ['face-popup'] }}
        doubleClick={{ disabled: false, excluded: ['face-popup'] }}
        panning={{ disabled: false, excluded: ['face-popup'] }}>
        <MediaPostHeader
          onBack={handleBack}
          onExpand={handleExpand}
          onShare={handleShare}
          onInfo={handleInfo}
          onStar={handleStar}
          onDelete={handleDeleteClick}
          onDownload={() => handleDownloads(mediaSrc)}
          mediaType={mediaType as MediaType}
          isFavorite={isFavorite}
          canShare={canShare}
          canDelete={canDelete}
          canDownload={canDownload}
          isPrivate={assetData?.data?.isPrivate}
          groupId={groupId}
          assetCreatedBy={assetData?.data?.createdBy}
          currentUserId={userId}
          canFavorite={canFavorite}
          isMediaOwner={assetData?.data?.mediaOwner}
          canFullScreen={!isPreviewError}
        />

        <main className="max-w-6xl mx-auto px-6 pb-10">
          <section className="bg-[var(--media-comment-bg)] rounded-md shadow-lg">
            <div className="relative bg-media-input">
              <div
                className={`w-full h-auto ${mediaType === 'video' ? 'min-h-[300px]' : ''} bg-media-input flex items-center justify-center overflow-hidden`}>
                {isPreviewError ? (
                  <div className="w-full flex flex-col items-center justify-center gap-4 py-20 bg-black text-white">
                    <p className="text-base font-medium">
                      {t('noPreviewAvailable')}
                    </p>
                    <Button
                      type="button"
                      btnStyle="primary"
                      onClick={() => handleDownloads(mediaSrc)}
                      className="px-5 py-2">
                      {t('download')}
                    </Button>
                  </div>
                ) : isPdf ? (
                  <div className="w-full h-[80vh] flex justify-center bg-black overflow-y-auto">
                    <div
                      ref={docPreviewRef}
                      className="w-full h-full max-w-5xl bg-white rounded-lg overflow-y-auto shadow-xl">
                      <DocViewer
                        documents={[
                          {
                            uri: mediaSrc,
                            fileType: 'pdf'
                          }
                        ]}
                        pluginRenderers={DocViewerRenderers}
                        config={{
                          header: {
                            disableHeader: true,
                            disableFileName: true
                          },
                          pdfZoom: {
                            defaultZoom: 1.0,
                            zoomJump: 0.2
                          }
                        }}
                        style={{
                          width: '100%',
                          height: '100%'
                        }}
                      />
                    </div>
                  </div>
                ) : isAudio ? (
                  <div className="w-full flex items-center justify-center py-20 bg-black">
                    <audio
                      src={mediaSrc}
                      controls
                      className="w-full max-w-2xl"
                      onError={handlePreviewError}
                    />
                  </div>
                ) : (
                  <div
                    className={`media-viewport ${mediaType === 'image' ? 'media-viewport--image' : ''}`}>
                    <div
                      ref={imageViewportScrollRef}
                      className={`media-viewport-scroll ${mediaType === 'image' ? 'media-viewport-scroll--image' : ''}`}>
                      <div
                        className="media-zoom-frame"
                        // style={{
                        //   width: imageFrameWidth ? `${imageFrameWidth}px` : '100%',
                        //   height: imageFrameHeight
                        //     ? `${imageFrameHeight}px`
                        //     : 'auto'
                        // }}
                      >
                        <div className="media-zoom-container w-full h-full flex items-center justify-center">
                          <TransformComponent
                            wrapperStyle={{ overflow: 'visible' }}>
                            <MediaPlayer
                              type={mediaType}
                              src={mediaSrc}
                              controls={true}
                              muted={true}
                              width="100%"
                              height="auto"
                              containerClassName="w-full h-auto block"
                              onImageLoad={onImageLoad}
                              onError={handlePreviewError}
                            />
                            {/* Face bounding boxes overlay */}
                            {mediaType === 'image' &&
                              mergedFaceDetections.length > 0 &&
                              !isSharing &&
                              assetData?.data?.mediaOwner && (
                                <div className="media-face-overlay">
                                  {mergedFaceDetections.map((face, idx) => {
                                    const box = face.boundingBox;
                                    const isActive = activeFaceIndex === idx;
                                    const isHovered = hoveredFaceIndex === idx;
                                    const hasPeopleId = Boolean(face.peopleId);

                                    // AWS Rekognition bounding box values are ratios (0-1), convert to pixel values
                                    const boxPx = {
                                      left:
                                        (box.left || 0) *
                                        imageDimensions?.naturalW,
                                      top:
                                        (box.top || 0) *
                                        imageDimensions?.naturalH,
                                      width:
                                        (box.width || 0) *
                                        imageDimensions?.naturalW,
                                      height:
                                        (box.height || 0) *
                                        imageDimensions?.naturalH
                                    };

                                    // Map bounding box to rendered image
                                    const diameter =
                                      Math.max(boxPx.width, boxPx.height) *
                                      imageDimensions?.scaleX;
                                    const centerX =
                                      (boxPx.left + boxPx.width / 2) *
                                        imageDimensions?.scaleX +
                                      imageDimensions?.offsetLeft;
                                    const centerY =
                                      (boxPx.top + boxPx.height / 2) *
                                        imageDimensions?.scaleY +
                                      imageDimensions?.offsetTop;
                                    const circleLeft = centerX - diameter / 2;
                                    const circleTop = centerY - diameter / 2;
                                    const containerW =
                                      imageDimensions?.renderedW || 0;
                                    const containerH =
                                      imageDimensions?.renderedH || 0;
                                    const availableRight =
                                      containerW - (circleLeft + diameter);
                                    const showPopupOnLeft =
                                      availableRight < 240;
                                    const clampedCenterY = Math.max(
                                      60,
                                      Math.min(containerH - 60, centerY)
                                    );

                                    return (
                                      <div
                                        key={idx}
                                        ref={
                                          isActive || isHovered
                                            ? facePopupRef
                                            : null
                                        }
                                        onClick={e => e.stopPropagation()}
                                        onMouseEnter={() =>
                                          setHoveredFaceIndex(idx)
                                        }
                                        onMouseLeave={() => {
                                          if (activeFaceIndex !== idx) {
                                            setHoveredFaceIndex(null);
                                          }
                                        }}>
                                        {/* Circular Bounding Box */}
                                        <div
                                          className="media-face-box"
                                          title="Click to tag or edit"
                                          style={{
                                            left: circleLeft + 'px',
                                            top: circleTop + 'px',
                                            width: diameter + 'px',
                                            aspectRatio: '1 / 1',
                                            cursor: 'pointer',
                                            borderColor: isActive
                                              ? 'white'
                                              : isHovered
                                                ? 'white'
                                                : 'transparent',
                                            borderWidth:
                                              isHovered || isActive
                                                ? '2px'
                                                : '0px',
                                            borderRadius: '50%'
                                          }}
                                          onClick={e => {
                                            e.stopPropagation();
                                            if (
                                              face.personName === null ||
                                              !hasPeopleId
                                            ) {
                                              // Toggle input for unidentified/unlinked faces
                                              handleFaceBoxClick(idx);
                                            } else {
                                              // Open edit mode for identified faces
                                              if (!isActive) {
                                                handleEditClick(
                                                  idx,
                                                  face.personName || ''
                                                );
                                              }
                                            }
                                          }}
                                        />

                                        {/* Show label when hovered or active */}
                                        {(isHovered || isActive) && (
                                          <div
                                            className="face-popup"
                                            style={{
                                              position: 'absolute',
                                              left: showPopupOnLeft
                                                ? circleLeft + 'px'
                                                : circleLeft + diameter + 'px',
                                              top: clampedCenterY + 'px',
                                              transform: showPopupOnLeft
                                                ? 'translate(calc(-100% - 5px), -50%)'
                                                : 'translate(5px, -50%)',
                                              pointerEvents: 'auto',
                                              zIndex: 20
                                            }}>
                                            {face.personName === null ||
                                            !hasPeopleId ? (
                                              <div className="flex flex-col gap-2 bg-white rounded-lg shadow-lg px-3 py-2 border border-gray-200">
                                                {/* Who is this - for unidentified faces */}
                                                <div className="flex items-center gap-2">
                                                  <span className="text-sm font-semibold text-[#49712B] whitespace-nowrap">
                                                    {t('whoIsThis')}
                                                  </span>
                                                  <div
                                                    onClick={e => {
                                                      e.stopPropagation();
                                                      handleFaceBoxClick(idx);
                                                    }}
                                                    className="w-6 h-6 rounded-full border-2 flex items-center justify-center cursor-pointer text-[#49712B] border-[#49712B] hover:bg-[#F9FEE9] transition-colors">
                                                    <span className="text-sm font-bold">
                                                      {isActive ? '-' : '+'}
                                                    </span>
                                                  </div>
                                                </div>

                                                {/* Input field when active */}
                                                {isActive && (
                                                  <div className="flex items-center gap-2">
                                                    <div className="w-[180px] media-sidebar-select-fix">
                                                      <SearchableSelectInput
                                                        name="name"
                                                        placeholder="Enter name"
                                                        isWhiteTheme
                                                        isFromCreatePeople={
                                                          true
                                                        }
                                                        isDisabled={isLoading}
                                                        value={searchText}
                                                        options={
                                                          familySearchOptions
                                                        }
                                                        onSearch={(
                                                          value: string
                                                        ) => {
                                                          if (
                                                            justSelectedRef.current
                                                          ) {
                                                            justSelectedRef.current =
                                                              false;
                                                            return;
                                                          }
                                                          setSearchText(value);
                                                          setIsNameSelected(
                                                            false
                                                          );
                                                          setSelectedPersonId(
                                                            ''
                                                          );
                                                        }}
                                                        onChange={(
                                                          selectedOption: any
                                                        ) => {
                                                          setSearchText(
                                                            selectedOption?.name ||
                                                              ''
                                                          );

                                                          // Only update if selecting from dropdown (has id)
                                                          if (
                                                            selectedOption?.id
                                                          ) {
                                                            justSelectedRef.current =
                                                              true;
                                                            setIsNameSelected(
                                                              true
                                                            );
                                                            setSelectedPersonId(
                                                              selectedOption?.id ||
                                                                ''
                                                            );
                                                          } else {
                                                            // User is just typing
                                                            setIsNameSelected(
                                                              false
                                                            );
                                                            setSelectedPersonId(
                                                              ''
                                                            );
                                                          }
                                                        }}
                                                      />
                                                    </div>
                                                    <Button
                                                      btnStyle="link"
                                                      type="button"
                                                      isDisabled={isLoading}
                                                      onClick={e => {
                                                        e.stopPropagation();
                                                        handleDoneClick(idx);
                                                      }}
                                                      className="px-3 py-2 !bg-[#F9FEE9] text-[var(--na-primary-color)] font-medium rounded-md text-sm border border-[#DCDCDC] hover:!bg-[#7F9D48] hover:!text-white">
                                                      {t('done')}
                                                    </Button>
                                                  </div>
                                                )}
                                              </div>
                                            ) : (
                                              <>
                                                {/* Show person name for identified faces */}
                                                {!isActive ? (
                                                  <div className="flex items-center gap-2 bg-white rounded-lg shadow-lg px-3 py-2 border border-gray-200">
                                                    {face.profileImage && (
                                                      <img
                                                        src={face.profileImage}
                                                        alt={face.personName}
                                                        className="w-6 h-6 rounded-full object-cover"
                                                      />
                                                    )}
                                                    <span className="text-sm font-semibold text-[#353535] whitespace-nowrap">
                                                      {face.personName}
                                                    </span>
                                                    <div
                                                      onClick={e => {
                                                        e.stopPropagation();
                                                        handleEditClick(
                                                          idx,
                                                          face.personName || ''
                                                        );
                                                      }}
                                                      className="w-5 h-5 flex items-center justify-center cursor-pointer text-[#353535] hover:text-[#7F9D48] transition-colors">
                                                      <SVGAssets.EditIcon className="w-4 h-4" />
                                                    </div>
                                                  </div>
                                                ) : (
                                                  <div className="flex flex-col gap-2 bg-white rounded-lg shadow-lg px-3 py-2 border border-gray-200">
                                                    {/* Edit mode for identified faces */}
                                                    <div className="flex items-center gap-2">
                                                      <span className="text-sm font-semibold text-[#49712B] whitespace-nowrap">
                                                        {t('tagPeople')}
                                                      </span>
                                                      <div
                                                        onClick={e => {
                                                          e.stopPropagation();
                                                          handleFaceBoxClick(
                                                            idx
                                                          );
                                                        }}
                                                        className="w-6 h-6 rounded-full border-2 flex items-center justify-center cursor-pointer text-[#49712B] border-[#49712B] hover:bg-[#F9FEE9] transition-colors">
                                                        <span className="text-sm font-bold">
                                                          -
                                                        </span>
                                                      </div>
                                                    </div>

                                                    {/* Input field when editing */}
                                                    <div className="flex items-center gap-2">
                                                      <div className="w-[180px] media-sidebar-select-fix">
                                                        <SearchableSelectInput
                                                          name="name"
                                                          placeholder="Enter name"
                                                          isWhiteTheme
                                                          isFromCreatePeople={
                                                            true
                                                          }
                                                          isDisabled={isLoading}
                                                          value={searchText}
                                                          options={
                                                            familySearchOptions
                                                          }
                                                          onSearch={(
                                                            value: string
                                                          ) => {
                                                            if (
                                                              justSelectedRef.current
                                                            ) {
                                                              justSelectedRef.current =
                                                                false;
                                                              return;
                                                            }
                                                            setSearchText(
                                                              value
                                                            );
                                                            setIsNameSelected(
                                                              false
                                                            );
                                                            setSelectedPersonId(
                                                              ''
                                                            );
                                                          }}
                                                          onChange={(
                                                            selectedOption: any
                                                          ) => {
                                                            setSearchText(
                                                              selectedOption?.name ||
                                                                ''
                                                            );

                                                            // In edit mode: only update if user selects from dropdown
                                                            if (
                                                              selectedOption?.id
                                                            ) {
                                                              // User selected from dropdown
                                                              justSelectedRef.current =
                                                                true;
                                                              setIsNameSelected(
                                                                true
                                                              );
                                                              setSelectedPersonId(
                                                                selectedOption?.id ||
                                                                  ''
                                                              );
                                                            } else {
                                                              // User is just typing
                                                              setIsNameSelected(
                                                                false
                                                              );
                                                              setSelectedPersonId(
                                                                ''
                                                              );
                                                            }
                                                          }}
                                                        />
                                                      </div>
                                                      <Button
                                                        btnStyle="link"
                                                        type="button"
                                                        isDisabled={isLoading}
                                                        onClick={e => {
                                                          e.stopPropagation();
                                                          handleDoneClick(idx);
                                                        }}
                                                        className="px-3 py-2 !bg-[#F9FEE9] text-[var(--na-primary-color)] font-medium rounded-md text-sm border border-[#DCDCDC] hover:!bg-[#7F9D48] hover:!text-white">
                                                        {t('done')}
                                                      </Button>
                                                    </div>
                                                  </div>
                                                )}
                                              </>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                          </TransformComponent>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <MediaStats
              likes={optimisticLikeCount}
              comments={optimisticCommentCount}
              shares={optimisticDiaryCount}
              isLiked={isLiked}
              onLike={handleLike}
              onComment={handleCommentClick}
              onShare={handleDiaryClick}
              isSharing={isSharing}
              isAssetOwner={assetData?.data?.createdBy === userId}
              isMediaOwner={assetData?.data?.mediaOwner}
            />
          </section>
          {isCommentSectionOpen && (
            <CommentSection
              comments={displayComments}
              currentUserId={userId}
              onAddComment={handleAddComment}
              onUpdateComment={handleUpdateComment}
              onDeleteComment={handleDeleteComment}
              onCommentLikeUpdate={handleCommentLikeUpdate}
            />
          )}

          {isDiarySectionOpen && (
            <DiarySection
              assetId={assetId}
              isOpen={isDiarySectionOpen}
              onDiaryUpdate={handleDiaryUpdate}
            />
          )}
        </main>
      </TransformWrapper>

      {/* Full Screen Modal */}
      <FullScreenModal
        isOpen={isFullScreenOpen}
        onClose={() => setIsFullScreenOpen(false)}
        mediaType={mediaType}
        mediaSrc={mediaSrc}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        variant="delete"
      />

      {/* Share Modal */}
      <ShareModalComponent
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        link={mediaSrc || ''}
        assetIds={[assetId]}
        mode="share"
        shareType="asset"
        onMessage={(type, message) =>
          showToast({ type, isAlert: true, alertProps: { type, message } })
        }
      />

      {/* Info Sidebar */}
      <MediaInfoSidebar
        isOpen={isInfoSidebarOpen}
        onClose={() => setIsInfoSidebarOpen(false)}
        onReopen={() => setIsInfoSidebarOpen(true)}
        assetId={assetId}
        isSharing={isSharing}
        groupId={groupId}
        refetch={() => {
          refetchAsset();
          refetchAssetPeople();
        }}
      />

      {/* Confirm creating a new person instead of renaming an existing one */}
      <ConfirmationModal
        isOpen={isConfirmCreateNewPersonOpen}
        onClose={handleCancelCreateNewPerson}
        onConfirm={handleConfirmCreateNewPerson}
        variant="warning"
        confirmButtonColor="bg-[#49712B]"
        confirmButtonHoverColor="hover:bg-[#49712B]"
        modelTitle={t('createNewPersonTitle')}
        message={t('createNewPersonConfirmMessage')}
        confirmText={t('yes')}
        cancelText={t('no')}
      />

      {/* Who is this Modals */}
      <SelectFriendFamily
        isOpen={isSelectFriendFamily}
        onClose={() => {
          setIsSelectFriendFamily(false);
          setName('');
          setCreatedPersonId('');
          setFaceIndexForNewPerson(null);
          setActiveFaceIndex(null);
          setSearchText('');
          setIsNameSelected(false);
          setSelectedPersonId('');
          setIsEditMode(false);
        }}
        onConfirm={handleFriendFamilyConfirm}
        personName={name}
      />

      <FriendsForm
        isOpen={isOpenFriendsForm}
        onClose={() => {
          setIsOpenFriendsForm(false);
          setName('');
          setCreatedPersonId('');
          setFaceIndexForNewPerson(null);
          setActiveFaceIndex(null);
          setSearchText('');
          setIsNameSelected(false);
          setSelectedPersonId('');
          setIsEditMode(false);
          refetchAsset();
          refetchAssetPeople();
        }}
        name={name}
        selectedPersonType={selectedPersonType}
        peopleFiltersHandler={peopleFiltersHandler}
        createdPersonId={createdPersonId}
        personToMove={null}
        setIsLoading={setIsLoading}
        onPersonCreated={handleNewPersonCreatedFromFace}
      />

      <FamilyForm
        isOpen={isOpenFamilyForm}
        onClose={() => {
          setIsOpenFamilyForm(false);
          setName('');
          setCreatedPersonId('');
          setFaceIndexForNewPerson(null);
          setActiveFaceIndex(null);
          setSearchText('');
          setIsNameSelected(false);
          setSelectedPersonId('');
          setIsEditMode(false);
          refetchAsset();
          refetchAssetPeople();
        }}
        name={name}
        selectedPersonType={selectedPersonType}
        peopleFiltersHandler={peopleFiltersHandler}
        personToMove={null}
        createdPersonId={createdPersonId}
        setIsLoading={setIsLoading}
        onPersonCreated={handleNewPersonCreatedFromFace}
      />

      {/* Merge Person Modal */}
      <MergePerson
        isOpen={isMergePersonOpen}
        isLoading={isLoading}
        onClose={() => {
          if (isLoading) return;
          setIsMergePersonOpen(false);
          setSourcePersonIdForMerge('');
          setSelectedMergePersonId('');
          setSelectedMergePerson('');
          setNewPersonImage('');
          setSelectedPersonImage('');
        }}
        onMerge={handleMergePerson}
        personName={name}
        selectedPersonName={selectedMergePerson}
        newPersonImage={newPersonImage}
        selectedPersonImage={selectedPersonImage}
      />
    </div>
  );
};

export default MediaPostScreen;
